import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useFormatter, useTranslations } from "next-intl";
import {
  COURSE_SLUG_MAX,
  COURSE_SLUG_MIN,
  COURSE_TITLE_MAX,
  suggestCourseSlug,
  validateCreateCourseForm,
  type CreateCourseFormErrors,
} from "@lms/backend/lib/courses/create-course-form";
import { usePublishCourse } from "@/components/instructor/use-publish-course";
import { Badge, Button, Field, Input, Select } from "@/components/ui";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type Course = {
  id: string;
  slug: string;
  title: string;
  courseType?: string;
  status: string;
  /** Distinct, non-revoked enrolments — from the staff course list. */
  enrolledStudentCount?: number;
  updatedAt?: string;
};

type InstructorOption = {
  userId: string;
  name: string | null;
  email: string;
};

type InstructorCoursesProps = {
  tenantSlug: string;
  initialCourses: Course[];
  isTenantAdmin?: boolean;
  instructorOptions?: InstructorOption[];
};

/**
 * The app configures no global time zone, so next-intl would log a fallback
 * warning per date. "Last updated" is about the viewer's own day, so say so.
 */
const VIEWER_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Search appears only once the list is long enough to need it. */
export const COURSE_SEARCH_THRESHOLD = 5;

const STATUS_TONE = { published: "success", draft: "neutral", archived: "warning" } as const;
const STATUS_LABEL = {
  published: "statusPublished",
  draft: "statusDraft",
  archived: "statusArchived",
} as const;
const TYPE_LABEL = { course: "typeCourse", retreat: "typeRetreat", bundle: "typeBundle" } as const;

function isKnown<T extends object>(
  map: T,
  key: string | undefined,
): key is Extract<keyof T, string> {
  return key !== undefined && Object.hasOwn(map, key);
}

export function InstructorCourses({
  tenantSlug,
  initialCourses,
  isTenantAdmin = false,
  instructorOptions = [],
}: InstructorCoursesProps) {
  const t = useTranslations("instructor");
  const tRoot = useTranslations();
  const errorText = (err: unknown, fallbackKey: string) =>
    apiErrorText(err, tRoot, `instructor.${fallbackKey}`, { namespace: "instructor.errors" });
  const tTools = useTranslations("instructorCourseTools");
  const tArchive = useTranslations("courseArchive");
  const publishCourse = usePublishCourse();
  const format = useFormatter();
  const navigate = useNavigate();
  const [courses, setCourses] = useState(initialCourses);
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState("");
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [courseType, setCourseType] = useState("course");
  const [location, setLocation] = useState("");
  const [instructorUserId, setInstructorUserId] = useState("");
  const [formErrors, setFormErrors] = useState<CreateCourseFormErrors | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cloningCourseId, setCloningCourseId] = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<{ id: string; message: string } | null>(null);
  const [deletingCourseId, setDeletingCourseId] = useState<string | null>(null);
  const [archivingCourseId, setArchivingCourseId] = useState<string | null>(null);
  const [publishingCourseId, setPublishingCourseId] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<{ id: string; message: string } | null>(null);

  const showSearch = courses.length > COURSE_SEARCH_THRESHOLD;
  const needle = query.trim().toLowerCase();
  const visibleCourses =
    showSearch && needle
      ? courses.filter((course) => course.title.toLowerCase().includes(needle))
      : courses;

  async function handleClone(course: Course) {
    // CRS-16: the copy is named in the instructor's language, not the server's "(Copy)";
    // F1: and the instructor may name it differently. The server picks a free slug.
    const defaultTitle = t("cloneTitle", { title: course.title }).slice(0, COURSE_TITLE_MAX);
    const answer = window.prompt(t("cloneTitlePrompt"), defaultTitle);
    if (answer === null) return;
    const copyTitle = (answer.trim() || defaultTitle).slice(0, COURSE_TITLE_MAX);
    setCloningCourseId(course.id);
    setError(null);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${course.id}/clone`, {
        method: "POST",
        body: JSON.stringify({ title: copyTitle }),
      });
      if (!res.ok) {
        setError(errorText(await readApiErrorResponse(res), "cloneFailed"));
        return;
      }
      const json = (await res.json()) as {
        data: { course: { id: string }; report?: unknown };
      };
      // CRS-6: the editor tells the instructor what the clone could not copy.
      navigate(`/t/${tenantSlug}/instructor/courses/${json.data.course.id}`, {
        state: json.data.report ? { cloneReport: json.data.report } : undefined,
      });
    } catch (err) {
      setError(errorText(err, "cloneFailed"));
    } finally {
      setCloningCourseId(null);
    }
  }

  /**
   * The same endpoints the builder's Publish/Unpublish button calls, so whatever
   * they refuse is refused here too. Not optimistic: the badge changes only on
   * the server's answer, and a refusal is shown on the row it belongs to.
   */
  async function handleTogglePublish(course: Course) {
    setPublishingCourseId(course.id);
    setPublishError(null);
    try {
      let updated: Partial<Course> | undefined;
      let nextStatus: string;
      if (course.status === "published") {
        const res = await apiFetch(`/v1/instructor/courses/${course.id}/unpublish`, {
          method: "POST",
        });
        if (!res.ok) {
          setPublishError({
            id: course.id,
            message: errorText(await readApiErrorResponse(res), "publishFailed"),
          });
          return;
        }
        const json = (await res.json().catch(() => ({}))) as {
          data?: { course?: Partial<Course> };
        };
        updated = json.data?.course;
        nextStatus = "draft";
      } else {
        // CRS-5: confirm naming the price (or that the course is free) before going live.
        const outcome = await publishCourse(course.id);
        if (outcome.status === "cancelled") return;
        if (outcome.status === "failed") {
          setPublishError({ id: course.id, message: errorText(outcome.error, "publishFailed") });
          return;
        }
        updated = outcome.course as Partial<Course>;
        nextStatus = "published";
      }
      setCourses((current) =>
        current.map((item) =>
          item.id === course.id
            ? {
                ...item,
                status: updated?.status ?? nextStatus,
                updatedAt: updated?.updatedAt ?? item.updatedAt,
              }
            : item,
        ),
      );
    } catch (err) {
      setPublishError({ id: course.id, message: errorText(err, "publishFailed") });
    } finally {
      setPublishingCourseId(null);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const errors = validateCreateCourseForm({ title, slug });
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors(null);
    const metadata = courseType === "retreat" && location ? { location } : undefined;

    const res = await apiFetch("/v1/instructor/courses", {
      method: "POST",
      body: JSON.stringify({
        slug: slug.trim(),
        title: title.trim(),
        courseType,
        metadata,
        ...(isTenantAdmin && instructorUserId ? { instructorUserId } : {}),
      }),
    });

    if (!res.ok) {
      const json = await res.json();
      setError(errorText(json, "createFailed"));
      return;
    }

    const json = await res.json();
    const course = json.data.course as Course;
    navigate(`/t/${tenantSlug}/instructor/courses/${course.id}`);
  }

  /**
   * Deleting a course cascades to its modules, lessons and progress (ISSUES_LOG 153 is the
   * incident where that surprised someone), so it is a two-step confirm that names the
   * course. PAY-2 / D4: only a tenant admin is offered Delete, and a course with payments or
   * students is refused by the server with a reason — shown translated, pointing at Archive.
   */
  async function handleDelete(course: Course) {
    setDeletingCourseId(course.id);
    setError(null);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${course.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const message = apiErrorText(
          await readApiErrorResponse(res),
          tRoot,
          "instructor.deleteFailed",
          {
            namespace: "courseArchive.errors",
          },
        );
        setDeleteError({ id: course.id, message });
        return;
      }
      setConfirmingDeleteId(null);
      setDeleteError(null);
      setCourses((current) => current.filter((item) => item.id !== course.id));
    } finally {
      setDeletingCourseId(null);
    }
  }

  /** PAY-2 / D4: retire a course without touching what students bought — they keep access. */
  async function handleArchive(course: Course) {
    setArchivingCourseId(course.id);
    setDeleteError(null);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${course.id}/archive`, {
        method: "POST",
      });
      if (!res.ok) {
        const message = apiErrorText(
          await readApiErrorResponse(res),
          tRoot,
          "courseArchive.failed",
          {
            namespace: "courseArchive.errors",
          },
        );
        setDeleteError({ id: course.id, message });
        return;
      }
      setConfirmingDeleteId(null);
      setCourses((current) =>
        current.map((item) => (item.id === course.id ? { ...item, status: "archived" } : item)),
      );
    } finally {
      setArchivingCourseId(null);
    }
  }

  // Live once the form has been submitted, so fixing a field clears its message.
  const liveErrors = formErrors ? validateCreateCourseForm({ title, slug }) : {};
  const titleError =
    liveErrors.title === "required"
      ? tTools("titleRequired")
      : liveErrors.title === "tooLong"
        ? tTools("titleTooLong", { max: COURSE_TITLE_MAX })
        : null;
  const slugError =
    liveErrors.slug === "tooShort"
      ? tTools("slugTooShort", { min: COURSE_SLUG_MIN })
      : liveErrors.slug === "tooLong"
        ? tTools("slugTooLong", { max: COURSE_SLUG_MAX })
        : liveErrors.slug === "invalid"
          ? tTools("slugInvalid")
          : null;

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-medium">{t("yourCourses")}</h2>
          <Button
            type="button"
            onClick={() => setShowCreate((open) => !open)}
            aria-expanded={showCreate}
            aria-controls="create-course-panel"
            variant={showCreate ? "secondary" : "primary"}
            data-testid="new-course-toggle"
          >
            {showCreate ? tTools("closeNewCourse") : tTools("newCourse")}
          </Button>
        </div>

        {showCreate ? (
          <form
            id="create-course-panel"
            onSubmit={handleCreate}
            noValidate
            className="space-y-4 rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-4"
            data-testid="create-course-form"
          >
            <h3 className="text-base font-medium">{t("createCourse")}</h3>
            <Field label={t("titleLabel")} error={titleError} required>
              <Input
                data-testid="create-course-title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!slugEdited) setSlug(suggestCourseSlug(e.target.value));
                }}
              />
            </Field>
            <Field label={t("slugLabel")} hint={tTools("slugHint")} error={slugError} required>
              <Input
                data-testid="create-course-slug"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  // Hand-edited slugs are left alone; clearing the field hands
                  // it back to the suggestion.
                  setSlugEdited(e.target.value !== "");
                }}
              />
            </Field>
            <Field label={t("typeLabel")}>
              <Select
                data-testid="create-course-type"
                value={courseType}
                onChange={(e) => setCourseType(e.target.value)}
              >
                <option value="course">{t("typeCourse")}</option>
                <option value="retreat">{t("typeRetreat")}</option>
                {/* CRS-19: "Bundle" is hidden until bundles are built (it did nothing). */}
              </Select>
            </Field>
            {courseType === "retreat" && (
              <Field label={t("locationLabel")}>
                <Input
                  data-testid="create-course-retreat-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </Field>
            )}
            {isTenantAdmin && instructorOptions.length > 0 && (
              <Field label={t("instructorLabel")}>
                <Select
                  data-testid="create-course-instructor"
                  value={instructorUserId}
                  onChange={(e) => setInstructorUserId(e.target.value)}
                >
                  <option value="">{t("assignToMe")}</option>
                  {instructorOptions.map((option) => (
                    <option key={option.userId} value={option.userId}>
                      {option.name ?? option.email}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            {error && (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {error}
              </p>
            )}
            <Button type="submit" data-testid="create-course-submit">
              {t("createCourse")}
            </Button>
          </form>
        ) : error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}

        {showSearch ? (
          <div className="flex flex-wrap items-end gap-2">
            <Field label={tTools("searchLabel")} className="min-w-0 flex-1 sm:max-w-sm">
              <Input
                type="search"
                data-testid="course-search"
                placeholder={tTools("searchPlaceholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </Field>
            {query ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setQuery("")}
                data-testid="course-search-clear"
              >
                {tTools("searchClear")}
              </Button>
            ) : null}
          </div>
        ) : null}

        <ul className="divide-y divide-[var(--border)] rounded-lg border border-[var(--border)]">
          {courses.length === 0 ? (
            <li className="px-4 py-6 text-sm text-[var(--text-muted)]">{t("noCourses")}</li>
          ) : visibleCourses.length === 0 ? (
            <li
              className="px-4 py-6 text-sm text-[var(--text-muted)]"
              data-testid="course-search-empty"
            >
              {tTools("searchNoMatches", { query: query.trim() })}
            </li>
          ) : (
            visibleCourses.map((course) => (
              // A course title is free text and can be long. The title column
              // is allowed to shrink (min-w-0) and wrap, the actions refuse to
              // shrink, and below `sm` the two stack instead of squeezing — so
              // the title can never run underneath Clone.
              <li
                key={course.id}
                className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
              >
                <div className="min-w-0">
                  <p className="font-medium break-words">{course.title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--text-muted)]">
                    <Badge
                      tone={
                        isKnown(STATUS_TONE, course.status) ? STATUS_TONE[course.status] : "neutral"
                      }
                      data-testid={`course-status-${course.slug}`}
                    >
                      {isKnown(STATUS_LABEL, course.status)
                        ? tTools(STATUS_LABEL[course.status])
                        : course.status}
                    </Badge>
                    {course.courseType ? (
                      <span>
                        {isKnown(TYPE_LABEL, course.courseType)
                          ? t(TYPE_LABEL[course.courseType])
                          : course.courseType}
                      </span>
                    ) : null}
                    {course.enrolledStudentCount !== undefined ? (
                      <span data-testid={`course-students-${course.slug}`}>
                        {tTools("studentCount", { count: course.enrolledStudentCount })}
                      </span>
                    ) : null}
                    {course.updatedAt ? (
                      <span data-testid={`course-updated-${course.slug}`}>
                        {tTools("updatedOn", {
                          date: format.dateTime(new Date(course.updatedAt), {
                            dateStyle: "medium",
                            timeZone: VIEWER_TIME_ZONE,
                          }),
                        })}
                      </span>
                    ) : null}
                  </div>
                  {publishError?.id === course.id ? (
                    <p
                      role="alert"
                      className="mt-1 text-sm text-[var(--danger)]"
                      data-testid={`publish-error-${course.slug}`}
                    >
                      {publishError.message}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => void handleTogglePublish(course)}
                    disabled={publishingCourseId === course.id}
                    className="text-sm text-[var(--text-heading)] hover:underline disabled:opacity-50"
                    data-testid={`toggle-publish-${course.slug}`}
                  >
                    {publishingCourseId === course.id
                      ? course.status === "published"
                        ? tTools("unpublishing")
                        : tTools("publishing")
                      : course.status === "published"
                        ? t("unpublish")
                        : t("publish")}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleClone(course)}
                    disabled={cloningCourseId === course.id}
                    className="text-sm text-[var(--text-muted)] hover:underline disabled:opacity-50"
                    data-testid={`clone-course-${course.slug}`}
                  >
                    {cloningCourseId === course.id ? t("cloning") : t("clone")}
                  </button>
                  <Link
                    to={`/t/${tenantSlug}/instructor/courses/${course.id}`}
                    className="text-sm text-[var(--text-heading)] hover:underline"
                    data-testid={`edit-course-${course.slug}`}
                  >
                    {t("edit")}
                  </Link>
                  {course.status !== "archived" ? (
                    <button
                      type="button"
                      onClick={() => void handleArchive(course)}
                      disabled={archivingCourseId === course.id}
                      className="text-sm text-[var(--text-muted)] hover:underline disabled:opacity-50"
                      data-testid={`archive-course-${course.slug}`}
                    >
                      {archivingCourseId === course.id
                        ? tArchive("archiving")
                        : tArchive("archive")}
                    </button>
                  ) : null}
                  {!isTenantAdmin ? null : confirmingDeleteId === course.id ? (
                    <span className="flex items-center gap-2 text-sm">
                      <span className="text-[var(--text-muted)]">{t("deleteConfirm")}</span>
                      <button
                        type="button"
                        onClick={() => void handleDelete(course)}
                        disabled={deletingCourseId === course.id}
                        className="font-semibold text-[var(--danger)] hover:underline disabled:opacity-50"
                        data-testid={`delete-course-confirm-${course.slug}`}
                      >
                        {deletingCourseId === course.id ? t("deleting") : t("deleteYes")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingDeleteId(null)}
                        className="text-[var(--text-muted)] hover:underline"
                        data-testid={`delete-course-cancel-${course.slug}`}
                      >
                        {t("cancel")}
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteError(null);
                        setConfirmingDeleteId(course.id);
                      }}
                      className="text-sm text-[var(--danger)] hover:underline"
                      data-testid={`delete-course-${course.slug}`}
                    >
                      {t("delete")}
                    </button>
                  )}
                  {deleteError?.id === course.id ? (
                    <span
                      role="alert"
                      className="text-sm text-[var(--danger)]"
                      data-testid={`delete-course-error-${course.slug}`}
                    >
                      {deleteError.message}
                    </span>
                  ) : null}
                  {course.status === "published" ? (
                    <Link
                      to={`/t/${tenantSlug}/learn/${course.slug}/community`}
                      className="text-sm text-[var(--text-heading)] hover:underline"
                      data-testid={`instructor-community-link-${course.slug}`}
                    >
                      {t("openCommunity")}
                    </Link>
                  ) : null}
                </div>
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
}
